import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Trash2, X, Plus, ExternalLink, Key, Shield, Copy, CheckCircle, AlertCircle, Info } from 'lucide-react';
import axios from 'axios';
import useFeatures from '../hooks/useFeatures';
import { motion, AnimatePresence } from "framer-motion";
import favicon from "../assets/favicon_new.png";

axios.defaults.baseURL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:5000';

const maskKey = (key) => {
  if (!key) return '';
  if (key.length <= 8) return '*'.repeat(key.length);
  const start = key.slice(0, 4);
  const end = key.slice(-4);
  return `${start}${'*'.repeat(key.length - 8)}${end}`;
};

const IntegrationsPage = ({ setActiveSection, isDarkMode = false, themeClasses = {}, embedded = false }) => {
  const navigate = useNavigate();
  const { hasIntegrationsFeature, getApiHeaders, loading } = useFeatures();

  const [apiKeys, setApiKeys] = useState([]);
  const [showMap, setShowMap] = useState({});
  const [isBusy, setIsBusy] = useState(false);
  const [copiedKeys, setCopiedKeys] = useState({});
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newApiKeyName, setNewApiKeyName] = useState("");
  const [newApiKeyPermissions, setNewApiKeyPermissions] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const headers = useMemo(() => getApiHeaders(), [getApiHeaders]);

  // Default theme classes if not provided
  const defaultThemeClasses = {
    background: isDarkMode ? 'bg-gray-900' : 'bg-gray-50',
    card: isDarkMode 
      ? 'bg-gray-800/50 border-gray-700/50 backdrop-blur-sm' 
      : 'bg-white/80 border-gray-200/50 shadow-sm backdrop-blur-sm',
    text: isDarkMode ? 'text-gray-100' : 'text-gray-900',
    subtext: isDarkMode ? 'text-gray-400' : 'text-gray-600',
    muted: isDarkMode ? 'text-gray-500' : 'text-gray-500',
  };

  const theme = { ...defaultThemeClasses, ...themeClasses };

  // Show success message
  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(""), 5000);
  };

  // Show error message
  const showError = (message) => {
    setErrorMessage(message);
    setTimeout(() => setErrorMessage(""), 5000);
  };

  // Fetch API keys from the server
  const fetchApiKeys = async () => {
    if (!hasIntegrationsFeature()) return;
    
    setIsLoading(true);
    try {
      const response = await axios.get('/api/keys', { headers });
      
      if (response.status === 200 && response.data?.api_keys) {
        setApiKeys(response.data.api_keys);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error('Failed to fetch API keys:', err);
      let errorMsg = 'Failed to fetch API keys. Please try again.';
      
      if (err.response?.status === 401) {
        errorMsg = 'Authentication failed. Please log in again.';
      } else if (err.response?.status === 403) {
        errorMsg = 'You do not have permission to view API keys.';
      } else if (err.response?.data?.error) {
        errorMsg = err.response.data.error;
      }
      
      showError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchApiKeys();
  }, [hasIntegrationsFeature, headers]);

  // Create new API key
  const onCreateKey = async () => {
    if (!newApiKeyName.trim()) {
      showError('API key name is required');
      return;
    }
    
    setIsBusy(true);
    setErrorMessage("");
    
    try {
      console.log('Creating API key with name:', newApiKeyName.trim());
      
      const requestBody = {
        name: newApiKeyName.trim()
      };
      
      // Add permissions if selected
      if (newApiKeyPermissions.length > 0) {
        requestBody.permissions = newApiKeyPermissions;
      }
      
      const response = await axios.post('/api/keys', requestBody, { 
        headers,
        timeout: 10000
      });
      
      if (response.status === 201 && response.data?.api_key) {
        const newKey = response.data.api_key;
        setApiKeys((prev) => [newKey, ...prev]);
        setShowCreateModal(false);
        setNewApiKeyName("");
        setNewApiKeyPermissions([]);
        showSuccess('API key created successfully!');
        
        // Show the full key to the user once
        setShowMap(prev => ({ ...prev, [newKey.id]: true }));
      } else {
        throw new Error('Invalid response format');
      }
      
    } catch (err) {
      console.error('Failed to create API key:', err);
      
      let errorMsg = 'Failed to create API key. Please try again.';
      
      if (err.response?.status === 400) {
        if (err.response.data?.error === 'Name is required') {
          errorMsg = 'API key name is required.';
        } else {
          errorMsg = err.response.data?.error || 'Invalid request data.';
        }
      } else if (err.response?.status === 401) {
        errorMsg = 'Authentication failed. Please log in again.';
      } else if (err.response?.status === 403) {
        errorMsg = 'You do not have permission to create API keys.';
      } else if (err.response?.status === 409) {
        errorMsg = 'An API key with this name already exists.';
      } else if (err.response?.status === 500) {
        errorMsg = 'Server error. Please try again later.';
      } else if (err.code === 'ECONNABORTED') {
        errorMsg = 'Request timed out. Please check your connection.';
      } else if (err.response?.data?.error) {
        errorMsg = err.response.data.error;
      }
      
      showError(errorMsg);
    } finally {
      setIsBusy(false);
    }
  };

  const openCreateModal = () => {
    setShowCreateModal(true);
    setNewApiKeyName("");
    setNewApiKeyPermissions([]);
    setErrorMessage("");
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setNewApiKeyName("");
    setNewApiKeyPermissions([]);
    setErrorMessage("");
  };

  // Delete API key
  const onDeleteKey = async (keyId) => {
    if (!window.confirm('Are you sure you want to delete this API key? This action cannot be undone.')) {
      return;
    }
    
    setIsBusy(true);
    
    try {
      const response = await axios.delete(`/api/keys/${keyId}`, { headers });
      
      if (response.status === 200) {
        setApiKeys((prev) => prev.filter((k) => k.id !== keyId));
        showSuccess('API key deleted successfully');
      } else {
        throw new Error('Failed to delete API key');
      }
    } catch (err) {
      console.error('Failed to delete API key:', err);
      
      let errorMsg = 'Failed to delete API key. Please try again.';
      
      if (err.response?.status === 401) {
        errorMsg = 'Authentication failed. Please log in again.';
      } else if (err.response?.status === 403) {
        errorMsg = 'You do not have permission to delete this API key.';
      } else if (err.response?.status === 404) {
        errorMsg = 'API key not found.';
      } else if (err.response?.status === 500) {
        errorMsg = 'Server error. Please try again later.';
      } else if (err.response?.data?.error) {
        errorMsg = err.response.data.error;
      }
      
      showError(errorMsg);
    } finally {
      setIsBusy(false);
    }
  };

  const copyToClipboard = async (text, keyId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKeys(prev => ({ ...prev, [keyId]: true }));
      showSuccess('API key copied to clipboard!');
      setTimeout(() => {
        setCopiedKeys(prev => ({ ...prev, [keyId]: false }));
      }, 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      showError('Failed to copy to clipboard. Please try again.');
    }
  };

  // Enhanced Button Component with better responsive design
  const Button = ({ 
    children, 
    onClick, 
    variant = "primary", 
    size = "md",
    disabled = false, 
    className = "",
    fullWidth = false 
  }) => {
    const baseClasses = "inline-flex items-center justify-center gap-2 font-medium rounded-lg transition-all duration-300 focus:ring-2 focus:ring-offset-2 focus:outline-none transform hover:scale-105 active:scale-95";
    
    const sizeClasses = {
      xs: "px-2 py-1 text-xs",
      sm: "px-3 py-1.5 text-sm",
      md: "px-4 py-2 text-sm",
      lg: "px-6 py-3 text-base"
    };

    const variantClasses = {
      primary: isDarkMode
        ? "bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-600/25 focus:ring-blue-500"
        : "bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-600/25 focus:ring-blue-500",
      secondary: isDarkMode
        ? "bg-gray-700 hover:bg-gray-600 text-gray-200 border border-gray-600"
        : "bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 shadow-sm",
      danger: isDarkMode
        ? "bg-red-600/20 hover:bg-red-600/30 text-red-400 border border-red-500/50"
        : "bg-red-50 hover:bg-red-100 text-red-600 border border-red-200",
      ghost: isDarkMode
        ? "hover:bg-gray-700 text-gray-400 hover:text-gray-200"
        : "hover:bg-gray-100 text-gray-600 hover:text-gray-900"
    };

    return (
      <button
        onClick={onClick}
        disabled={disabled}
        className={`${baseClasses} ${sizeClasses[size]} ${variantClasses[variant]} ${
          disabled ? 'opacity-50 cursor-not-allowed transform-none' : ''
        } ${fullWidth ? 'w-full' : ''} ${className}`}
      >
        {children}
      </button>
    );
  };

  // Enhanced Card Component with better responsive margins
  const Card = ({ children, className = "", ...props }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={`${theme.card} rounded-xl p-4 sm:p-6 hover:shadow-lg transition-all duration-300 ${className}`}
      {...props}
    >
      {children}
    </motion.div>
  );

  // Responsive Message Component
  const Message = ({ type, message, onClose }) => {
    if (!message) return null;
    
    const bgColor = type === 'error' 
      ? (isDarkMode ? 'bg-red-900/20 border-red-500/50' : 'bg-red-50 border-red-200')
      : (isDarkMode ? 'bg-green-900/20 border-green-500/50' : 'bg-green-50 border-green-200');
    
    const textColor = type === 'error'
      ? (isDarkMode ? 'text-red-400' : 'text-red-700')
      : (isDarkMode ? 'text-green-400' : 'text-green-700');
    
    const icon = type === 'error' ? <AlertCircle size={16} /> : <CheckCircle size={16} />;
    
    return (
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className={`${bgColor} border rounded-lg p-3 mb-4 flex items-start sm:items-center justify-between gap-3`}
      >
        <div className="flex items-start sm:items-center space-x-2 flex-1">
          <div className="flex-shrink-0 mt-0.5 sm:mt-0">
            {icon}
          </div>
          <span className={`text-sm font-medium break-words ${textColor}`}>{message}</span>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className={`${textColor} hover:opacity-70 transition-opacity flex-shrink-0 mt-0.5 sm:mt-0`}
          >
            <X size={16} />
          </button>
        )}
      </motion.div>
    );
  };

  if (loading) {
    return (
      <div className={`fixed inset-0 flex items-center justify-center ${isDarkMode?"bg-gray-900/80": "bg-white/80"}  backdrop-blur-sm z-50`}>
        <span className="flex items-center space-x-3 px-4">
          <div className="relative w-7 h-7 flex-shrink-0">
            <motion.div
              className="w-full h-full"
              animate={{ rotate: 360 }}
              transition={{
                duration: 2,
                ease: "linear",
                repeat: Infinity,
              }}
            >
              <img
                src={favicon}
                alt="Loading"
                className="w-full h-full object-contain"
                onError={(e) => {
                  console.log("Image failed to load:", e.target.src);
                  e.target.style.display = 'none';
                }}
              />
            </motion.div>
          </div>
          <span className={`text-lg font-medium ${theme.text} text-center sm:text-left`}>
            Loading integrations...
          </span>
        </span>
      </div>
    );
  }

  if (!hasIntegrationsFeature()) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[70vh] px-4 sm:px-6 text-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="max-w-md mx-auto"
        >
          <div className={`p-4 rounded-full ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} w-fit mx-auto mb-6`}>
            <Shield className={`h-8 w-8 sm:h-12 sm:w-12 ${theme.subtext}`} />
          </div>
          <h2 className={`text-xl sm:text-2xl font-semibold mb-4 ${theme.text}`}>
            Integrations Not Available
          </h2>
          <p className={`mb-8 leading-relaxed text-sm sm:text-base ${theme.subtext}`}>
            Your current plan does not include access to integrations. Upgrade your plan to connect with external tools and access API features.
          </p>
          <Button onClick={() => navigate('/dashboard')} variant="primary" size="lg" fullWidth>
            Go to Dashboard
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className={`${embedded ? '' : `min-h-screen ${theme.background}`} transition-colors duration-300`}>
      <div className={`${embedded ? '' : 'max-w-7xl mx-auto'} px-3 sm:px-4 lg:px-8 py-4 sm:py-8 space-y-6 sm:space-y-8`}>

        {/* Messages */}
        <div className="w-full">
          <Message type="error" message={errorMessage} onClose={() => setErrorMessage("")} />
          <Message type="success" message={successMessage} onClose={() => setSuccessMessage("")} />
        </div>

        {/* Connect Tools Section - Responsive */}
        <div className="flex justify-center items-center px-2">
          <Button
            onClick={() => {
              if (setActiveSection) {
                setActiveSection('support');
              } else {
                window.location.hash = 'support';
              }
            }}
            className={`!bg-blue-600 px-4 sm:px-6 py-3 sm:py-4 text-base sm:text-lg ${isDarkMode?"text-white":"text-white"} w-full sm:w-auto max-w-sm sm:max-w-none`}
            variant="secondary"
          >
            <span className="hidden sm:inline">Connect Your Favorite Tool</span>
            <span className="sm:hidden">Connect Tool</span>
            <ExternalLink size={16} className="flex-shrink-0" />
          </Button>
        </div>

        {/* API Keys Section */}
        <Card>
          <div className="space-y-4 sm:space-y-6">
            {/* Header - Responsive */}
            <div className="flex flex-col space-y-4 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start space-x-3">
                <div className="flex-1 min-w-0">
                  <h2 className={`text-lg sm:text-xl font-semibold ${theme.text}`}>
                    API Keys
                  </h2>
                  <p className={`text-xs sm:text-sm ${theme.subtext} mt-1`}>
                    Manage your API keys for programmatic access to ClevrScan services
                  </p>
                </div>
              </div>
              
              {/* Action buttons - Responsive stack */}
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 w-full sm:w-auto">
                <Button
                  onClick={fetchApiKeys}
                  disabled={isLoading}
                  variant="secondary"
                  size="sm"
                  fullWidth
                  className="sm:w-auto order-2 sm:order-1"
                >
                  <div className="relative w-4 h-4 mr-2 flex-shrink-0">
                    {isLoading ? (
                      <motion.div
                        className="w-full h-full"
                        animate={{ rotate: 360 }}
                        transition={{
                          duration: 2,
                          ease: "linear",
                          repeat: Infinity,
                        }}
                      >
                        <img
                          src={favicon}
                          alt="Loading"
                          className="w-full h-full object-contain"
                          onError={(e) => {
                            console.log('Image failed to load:', e.target.src);
                            e.target.style.display = 'none';
                          }}
                        />
                      </motion.div>
                    ) : (
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                        />
                      </svg>
                    )}
                  </div>
                  Refresh
                </Button>
                <Button
                  onClick={openCreateModal}
                  disabled={isBusy}
                  variant="primary"
                  fullWidth
                  className="sm:w-auto order-1 sm:order-2"
                >
                  <Plus size={16} className="flex-shrink-0" />
                  <span className="hidden sm:inline">Create API Key</span>
                  <span className="sm:hidden">Create Key</span>
                </Button>
              </div>
            </div>

            {/* API Keys Table - Enhanced Responsive */}
            <div className={`border rounded-lg overflow-hidden ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              {/* Table Header - Hidden on small screens, visible on larger */}
              <div className={`hidden lg:grid lg:grid-cols-12 px-4 py-3 text-sm font-medium border-b ${
                isDarkMode 
                  ? 'bg-gray-800/50 border-gray-700 text-gray-300' 
                  : 'bg-gray-50 border-gray-200 text-gray-700'
              }`}>
                <div className="col-span-3">Name</div>
                <div className="col-span-4">API Key</div>
                <div className="col-span-2">Created</div>
                <div className="col-span-2">Last Used</div>
                <div className="col-span-1 text-right">Actions</div>
              </div>

              {/* Table Body */}
              <AnimatePresence>
                {isLoading ? (
                  <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="px-4 py-8 text-center"
                  >
                    <div className="relative w-7 h-7 mx-auto mb-4">
                      <motion.div
                        className="w-full h-full"
                        animate={{ rotate: 360 }}
                        transition={{
                          duration: 2,
                          ease: "linear",
                          repeat: Infinity,
                        }}
                      >
                        <img
                          src={favicon}
                          alt="Loading"
                          className="w-full h-full object-contain"
                          onError={(e) => {
                            console.log('Image failed to load:', e.target.src);
                            e.target.style.display = 'none';
                          }}
                        />
                      </motion.div>
                    </div>
                    <p className={`text-sm ${theme.subtext}`}>
                      Loading API keys...
                    </p>
                  </motion.div>
                ) : apiKeys.length === 0 ? (
                  <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="px-4 py-8 text-center"
                  >
                    <Key className={`h-8 w-8 sm:h-12 sm:w-12 mx-auto mb-4 ${theme.subtext}`} />
                    <p className={`text-sm ${theme.subtext} px-4`}>
                      No API keys yet. Create your first API key to get started.
                    </p>
                  </motion.div>
                ) : (
                  apiKeys.map((k, index) => {
                    const isShown = !!showMap[k.id];
                    const displayKey = isShown ? k.key : maskKey(k.key);
                    const created = k.created_at ? new Date(k.created_at) : null;
                    const lastUsed = k.last_used_at ? new Date(k.last_used_at) : null;
                    const createdStr = created 
                      ? created.toLocaleDateString(undefined, { 
                          day: '2-digit', 
                          month: 'short', 
                          year: 'numeric' 
                        })
                      : '-';
                    const lastUsedStr = lastUsed 
                      ? lastUsed.toLocaleDateString(undefined, { 
                          day: '2-digit', 
                          month: 'short', 
                          year: 'numeric' 
                        })
                      : 'Never';
                    const isCopied = copiedKeys[k.id];

                    return (
                      <motion.div
                        key={k.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className={`border-b last:border-b-0 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}
                      >
                        {/* Desktop Layout - Large screens only */}
                        <div className={`hidden lg:grid lg:grid-cols-12 items-center px-4 py-4 text-sm hover:bg-opacity-50 transition-colors ${
                          isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                        }`}>
                          <div className={`col-span-3 font-medium truncate pr-2 ${theme.text}`}>
                            {k.name || 'API Key'}
                          </div>
                          <div className="col-span-4 flex items-center space-x-2 pr-2">
                            <code className={`font-mono text-sm px-2 py-1 rounded flex-1 min-w-0 ${
                              isDarkMode 
                                ? 'bg-gray-800 text-gray-300 border border-gray-700' 
                                : 'bg-gray-100 text-gray-700 border border-gray-200'
                            }`}>
                              <span className="truncate block">{displayKey}</span>
                            </code>
                            <Button
                              onClick={() => setShowMap((s) => ({ ...s, [k.id]: !isShown }))}
                              variant="ghost"
                              size="xs"
                              className="p-1.5 flex-shrink-0"
                            >
                              {isShown ? <EyeOff size={14} /> : <Eye size={14} />}
                            </Button>
                            {isShown && (
                              <Button
                                onClick={() => copyToClipboard(k.key, k.id)}
                                variant="ghost"
                                size="xs"
                                className="p-1.5 flex-shrink-0"
                              >
                                {isCopied ? <CheckCircle size={14} className="text-green-500" /> : <Copy size={14} />}
                              </Button>
                            )}
                          </div>
                          <div className={`col-span-2 ${theme.subtext} pr-2`}>
                            {createdStr}
                          </div>
                          <div className={`col-span-2 ${theme.subtext} pr-2`}>
                            {lastUsedStr}
                          </div>
                          <div className="col-span-1 text-right">
                            <Button
                              onClick={() => onDeleteKey(k.id)}
                              disabled={isBusy}
                              variant="danger"
                              size="xs"
                              className="p-1.5"
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </div>

                        {/* Mobile/Tablet Layout - Enhanced */}
                        <div className={`lg:hidden px-3 sm:px-4 py-4 space-y-3 ${
                          isDarkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'
                        }`}>
                          {/* Header row with name and date */}
                          <div className="flex items-start justify-between gap-3">
                            <h3 className={`font-medium text-sm sm:text-base flex-1 min-w-0 ${theme.text}`}>
                              <span className="truncate block">{k.name || 'API Key'}</span>
                            </h3>
                            <span className={`text-xs sm:text-sm ${theme.subtext} flex-shrink-0`}>
                              {createdStr}
                            </span>
                          </div>
                          
                          {/* API Key display section */}
                          <div className="space-y-2">
                            <div className="flex items-center space-x-2">
                              <code className={`font-mono text-xs sm:text-sm px-2 py-1.5 rounded flex-1 min-w-0 ${
                                isDarkMode 
                                  ? 'bg-gray-800 text-gray-300 border border-gray-700' 
                                  : 'bg-gray-100 text-gray-700 border border-gray-200'
                              }`}>
                                <span className="truncate block">{displayKey}</span>
                              </code>
                              <div className="flex space-x-1 flex-shrink-0">
                                <Button
                                  onClick={() => setShowMap((s) => ({ ...s, [k.id]: !isShown }))}
                                  variant="ghost"
                                  size="xs"
                                  className="p-1.5"
                                >
                                  {isShown ? <EyeOff size={14} /> : <Eye size={14} />}
                                </Button>
                                {isShown && (
                                  <Button
                                    onClick={() => copyToClipboard(k.key, k.id)}
                                    variant="ghost"
                                    size="xs"
                                    className="p-1.5"
                                  >
                                    {isCopied ? <CheckCircle size={14} className="text-green-500" /> : <Copy size={14} />}
                                  </Button>
                                )}
                              </div>
                            </div>
                            
                            {/* Bottom row with last used and delete button */}
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-2 sm:space-y-0">
                              <span className={`text-xs sm:text-sm ${theme.subtext}`}>
                                Last used: {lastUsedStr}
                              </span>
                              <Button
                                onClick={() => onDeleteKey(k.id)}
                                disabled={isBusy}
                                variant="danger"
                                size="sm"
                                className="w-full sm:w-auto"
                              >
                                <Trash2 size={14} />
                                Delete
                              </Button>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })
                )}
              </AnimatePresence>
            </div>
          </div>
        </Card>

        {/* API Documentation Section - Enhanced Responsive */}
        <Card>
          <div className="space-y-4">
            <div className="flex flex-col space-y-4 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between">
              <div className="space-y-2 flex-1 min-w-0">
                <h2 className={`text-lg sm:text-xl font-semibold ${theme.text}`}>
                  API Documentation
                </h2>
                <p className={`text-sm sm:text-base ${theme.subtext}`}>
                  Learn how to use our APIs with examples and detailed guides
                </p>
              </div>
              <Button 
                variant="secondary" 
                className="w-full sm:w-auto flex-shrink-0"
                onClick={() => window.open('https://docs.clevrscan.com/', '_blank')}
              >
                <span className="hidden sm:inline">View Documentation</span>
                <span className="sm:hidden">View Docs</span>
                <ExternalLink size={16} className="flex-shrink-0" />
              </Button>
            </div>
          </div>
        </Card>

        {/* Create API Key Modal - Enhanced Responsive */}
        <AnimatePresence>
          {showCreateModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-3 sm:p-4"
              onClick={closeCreateModal}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className={`${theme.card} rounded-xl p-4 sm:p-6 w-full max-w-md mx-auto max-h-[90vh] overflow-y-auto`}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-4 sm:mb-6">
                  <h3 className={`text-lg sm:text-xl font-semibold ${theme.text} pr-4`}>
                    Create New API Key
                  </h3>
                  <button
                    onClick={closeCreateModal}
                    className={`p-1 rounded-lg hover:bg-opacity-20 transition-colors flex-shrink-0 ${
                      isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'
                    }`}
                  >
                    <X size={20} className={theme.subtext} />
                  </button>
                </div>
                
                <div className="space-y-4 sm:space-y-6">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${theme.text}`}>
                      API Key Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={newApiKeyName}
                      onChange={(e) => setNewApiKeyName(e.target.value)}
                      placeholder="Enter a name for your API key"
                      className={`w-full px-3 py-2.5 sm:py-2 rounded-lg border transition-colors text-sm sm:text-base ${
                        isDarkMode
                          ? 'bg-gray-700/50 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500'
                          : 'bg-gray-50 border-gray-200 text-gray-800 placeholder-gray-500 focus:border-blue-500'
                      } focus:ring-2 focus:ring-blue-500/20 focus:outline-none`}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && newApiKeyName.trim()) {
                          onCreateKey();
                        }
                      }}
                      autoFocus
                    />
                  </div>
                  
                  <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3 pt-2">
                    <Button
                      onClick={closeCreateModal}
                      variant="secondary"
                      className="flex-1 order-2 sm:order-1"
                      size="md"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={onCreateKey}
                      disabled={isBusy || !newApiKeyName.trim()}
                      variant="primary"
                      className="flex-1 order-1 sm:order-2"
                      size="md"
                    >
                      {isBusy ? (
                        <>
                          <div className="relative w-4 h-4 flex-shrink-0">
                            <motion.div
                              className="w-full h-full"
                              animate={{ rotate: 360 }}
                              transition={{
                                duration: 2,
                                ease: "linear",
                                repeat: Infinity,
                              }}
                            >
                              <img
                                src={favicon}
                                alt="Loading"
                                className="w-full h-full object-contain"
                                onError={(e) => {
                                  console.log('Image failed to load:', e.target.src);
                                  e.target.style.display = 'none';
                                }}
                              />
                            </motion.div>
                          </div>
                          <span className="hidden sm:inline">Creating...</span>
                          <span className="sm:hidden">Creating...</span>
                        </>
                      ) : (
                        <>
                          <span className="hidden sm:inline">Create Key</span>
                          <span className="sm:hidden">Create</span>
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default IntegrationsPage;